import * as React from "react";
import { DatePicker } from "../../../../../../../react-core/Datepicker/DatePicker";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { addWeeks } from "../../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { FrequencyTypeArrays, FrequencyArraysMap, datePickerOptions } from "../../Resource/Contents";
import { SectionHeader } from "../../SharedComponents";
import { BusinessDaysAction } from "../../../../Common/BusinessDays/Actions/BusinessDaysAction";

interface IRegularCrystallisationProps extends IProcessComponentProps {
    data: DrawdownModel;
    process: ProcessType;
    doCalculation: VoidFunction;
}

interface IRegularCrystallisationState {
    minimumDate?: Date;
    isLoading?: boolean;
}

export class RegularCrystallisation extends ProcessComponent<
    IRegularCrystallisationProps,
    IRegularCrystallisationState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            isLoading: false,
        };
    }

    private onChangeRegularCrystallisation(changes) {
        this.onDataChange(this.props.onChange, changes, this.props.data, "RegularCrystallisationDetails");
    }

    componentDidMount() {
        const { data } = this.props;
        super.componentDidMount();
        if (!data?.RegularCrystallisationDetails?.FirstPaymentDay) {
            this.onChangeRegularCrystallisation({
                FirstPaymentDay: JsonDateParser.toJsonDateNoOffset(addWeeks(new Date(), 2)),
            });
        }

        BusinessDaysAction.GetBusinessDays(new Date(), true, 10, true, (callBack: Date) => {
            this.setState({
                minimumDate: callBack,
                isLoading: true,
            });
        });
    }

    private renderRegularCrystallisationDetails(): JSX.Element {
        const { data, process } = this.props;
        if (!this.state.isLoading) {return null;}
        return (
            <>
                <RadiosEditor
                    options={[true, false]}
                    cssWrapper={"rg-inline mb-10"}
                    onChange={(v) =>
                        this.onChangeRegularCrystallisation({ HasADifferentInitialAmount: v, InitialAmount: null })
                    }
                    selected={data?.RegularCrystallisationDetails?.HasADifferentInitialAmount}
                    label="Specify a different initial crystallisation amount?"
                    getText={(o) => (o ? "Yes" : "No")}
                    required={"Please select"}
                />
                {data?.RegularCrystallisationDetails?.HasADifferentInitialAmount && (
                    <MoneyTextBoxEditor
                        cssWrapper="control-size-245"
                        label="Initial regular Crystallisation amount"
                        currency={CurrencyCode.GBP}
                        value={data?.RegularCrystallisationDetails?.InitialAmount?.Amount}
                        required={"Please enter"}
                        onChange={(v) =>
                            this.onChangeRegularCrystallisation({
                                InitialAmount: { Amount: v, Currency: CurrencyCode.GBP },
                            })
                        }
                        onBlur={(v) => this.props.doCalculation()}
                        validationRules={[
                            {
                                errorMessage: "Please enter an amount that is greater than zero.",
                                validator: (v) => v > 0,
                            },
                        ]}
                    />
                )}
                <MoneyTextBoxEditor
                    cssWrapper="control-size-245"
                    label="Regular crystallisation amount"
                    currency={CurrencyCode.GBP}
                    value={data?.RegularCrystallisationDetails?.Amount?.Amount}
                    required={"Please enter"}
                    onChange={(v) =>
                        this.onChangeRegularCrystallisation({ Amount: { Amount: v, Currency: CurrencyCode.GBP } })
                    }
                    onBlur={(v) => this.props.doCalculation()}
                    validationRules={[
                        {
                            errorMessage: "Please enter an amount that is greater than zero.",
                            validator: (v) => v > 0,
                        },
                    ]}
                />
                <SelectEditor
                    options={FrequencyTypeArrays}
                    getText={(v) => {
                        return FrequencyArraysMap[v];
                    }}
                    cssWrapper="control-size-245"
                    required="Please select payment frequency"
                    placeholder="Please select"
                    selected={data?.RegularCrystallisationDetails?.Frequency}
                    label="Payment frequency"
                    onChange={(v) => this.onChangeRegularCrystallisation({ Frequency: v })}
                    onBlur={(v) => this.props.doCalculation()}
                />
                {false && (
                    <Alert
                        alertType={AlertType.Warning}
                        message={
                            <>
                                <p>
                                    Changing the payment date / frequency will amend any regular pension income also
                                    associated with this pension.
                                </p>
                                <p>
                                    Please review the income required from the pension through 'Edit Income Payments'
                                    process following acceptance of this instruction.
                                </p>
                            </>
                        }
                    />
                )}
                <div className="row">
                    <div className="col-md-4">
                        <DatePicker
                            filterDate={{
                                errorMessage: "Date must also be between 1-28 of any month",
                                validator: (v) => {
                                    if (v.getDate() > 28) {return false;}
                                    return true;
                                },
                            }}
                            label="First payment date"
                            selected={JsonDateParser.toDateNoOffset(
                                data?.RegularCrystallisationDetails?.FirstPaymentDay
                            )}
                            moreThan={{
                                errorMessage: "Please enter a date that is at least 10 working days from today.",
                                value: new Date(this.state.minimumDate),
                            }}
                            onChange={(v) =>
                                this.onChangeRegularCrystallisation({
                                    FirstPaymentDay: JsonDateParser.toJsonDateNoOffset(v),
                                })
                            }
                            cssWrapper="control-size-245"
                            alwaysValidateOnUpdate={false}
                            {...datePickerOptions}
                            required="Please select first payment date"
                        />
                    </div>
                </div>
            </>
        );
    }

    public render(): JSX.Element {
        return (
            <>
                {SectionHeader("Regular crystallisation details")}
                {this.renderRegularCrystallisationDetails()}
            </>
        );
    }
}
